import { DropdownMenu } from 'radix-ui';
import { Link } from 'react-router-dom';
import { Bell, Copy } from 'lucide-react';
import { useEntity } from '@/hooks/useEntity';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { DATA_ROOM_NOTIFICATION_COPY as DATA_ROOM_COPY } from '@/lib/constants/notifications';
import { dayCount, formatDate, formatDateTime, wholeDaysBetween } from '@/lib/utils/date.utils';
import { displayNameFromEmail } from '@/lib/utils/user.utils';
import { toast } from '@/lib/toast';
import { useNotificationsPoll } from '../hooks/useNotificationsPoll';
import { useNotificationActions } from '../hooks/useNotificationActions';
import type {
  AppNotification,
  ProfileViewNotification,
  MemberJoinedNotification,
  JoinRequestedNotification,
  OfferReviewedNotification,
  DataRoomPremiumGrantedNotification,
  DataRoomPremiumReminderNotification,
} from '../api/notifications.api';

function displayName(notification: ProfileViewNotification): string {
  if (!notification.recipientEmail) return 'Someone';
  return displayNameFromEmail(notification.recipientEmail);
}

/**
 * Copy for each notification type, computed once per type rather than
 * branched on `notification.type` at every render site (Style Rules - no
 * repeated ternary/if-chains on the same discriminant).
 */
const NOTIFICATION_COPY: Record<AppNotification['type'], (n: AppNotification) => string> = {
  profile_viewed: (n) => {
    const view = n as ProfileViewNotification;
    // Prefer the real name (Task 4 added ProfileShareRecipient.recipientName);
    // fall back to the email-derived heuristic only for recipients created
    // before that field existed.
    const who = view.recipientName || displayName(view);
    return view.isRepeatView ? `${who} viewed your profile again` : `${who} viewed your profile`;
  },
  member_joined: (n) => {
    const joined = n as MemberJoinedNotification;
    return `${joined.newMemberName ?? 'A new member'} joined your team`;
  },
  join_requested: (n) => {
    const requested = n as JoinRequestedNotification;
    const who = requested.joinRequesterName || requested.joinRequesterEmail || 'Someone';
    return `${who}, a user from your organization, wants to join`;
  },
  offer_accepted: (n) => `Your offer${offerTitleSuffix(n)} was accepted`,
  // "Needs changes" rather than "was rejected": a rejected offer is editable
  // and returns to Draft when the partner edits it (MYN-106 AC6), so the copy
  // has to read as an action to take, not a door closing.
  offer_rejected: (n) => `Your offer${offerTitleSuffix(n)} needs changes`,
  data_room_premium_granted: (n) => {
    const endsAt = (n as DataRoomPremiumGrantedNotification).accessEndsAt;
    // Without a date the sentence still says the useful part; a dangling "until" would not.
    return endsAt
      ? DATA_ROOM_COPY.PREMIUM_GRANTED.replace('{date}', formatDate(endsAt))
      : DATA_ROOM_COPY.PREMIUM_GRANTED_UNDATED;
  },
  // FR-137. The days are counted from when the reminder was sent, so a
  // "30 days" reminder still says 30 when it is read a week later.
  data_room_premium_expiring: (n) => {
    const reminder = n as DataRoomPremiumReminderNotification;
    const days = wholeDaysBetween(reminder.createdAt, reminder.accessEndsAt);
    return days === null
      ? DATA_ROOM_COPY.PREMIUM_EXPIRING_UNDATED
      : DATA_ROOM_COPY.PREMIUM_EXPIRING.replace('{days}', dayCount(days));
  },
  data_room_premium_expired: () => DATA_ROOM_COPY.PREMIUM_EXPIRED,
};


/**
 * ` "Free Cap Table Review"` when the row carries a title, an empty string
 * when it does not - the title is joined on the Backend and is null for an
 * offer since deleted, and an empty pair of quotes reads as a bug.
 */
function offerTitleSuffix(notification: AppNotification): string {
  const title = (notification as OfferReviewedNotification).offerTitle;
  return title ? ` "${title}"` : '';
}

/** Shown if the Backend raises a type this build has no copy for - see the render site. */
const UNKNOWN_NOTIFICATION_COPY = 'You have a new notification';

/**
 * Notification types that navigate somewhere when clicked - only when they
 * actually carry a `linkPath` (older `member_joined` rows created before
 * that field existed, or a defensive null, have nowhere to send the user;
 * treat those as plain, non-navigating rows instead of a dead `#` link).
 */
function isJoinRequestedNotification(
  notification: AppNotification,
): notification is JoinRequestedNotification & { joinRequesterEmail: string } {
  return notification.type === 'join_requested' && Boolean(notification.joinRequesterEmail);
}

async function copyEmail(email: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard.');
  } catch {
    toast.error('Could not copy the email. Please copy it manually.');
  }
}

function isLinkedNotification(
  notification: AppNotification,
): notification is (
  | MemberJoinedNotification
  | JoinRequestedNotification
  | OfferReviewedNotification
  | DataRoomPremiumGrantedNotification
) & {
  linkPath: string;
} {
  // Tested on the field itself rather than an allowlist of types that carry
  // it: the allowlist went stale the moment the offer types shipped with a
  // `linkPath` of their own, and those rows rendered as dead text.
  return 'linkPath' in notification && Boolean(notification.linkPath);
}

/**
 * Dashboard alert bell (MYN-55, MYN-56) - lives in the global `AppNavbar`
 * (via its `rightSlot`) so an admin sees a new profile view or team-join
 * from anywhere in the app, not only on `/dashboard`. No Figma mock exists
 * for this component; built from existing tokens/primitives only - flag for
 * visual sign-off at the next design pass. Renders nothing for a viewer no
 * notification type can reach.
 *
 * The gate mirrors the API's own (`NotificationsController`'s ANY-of), which
 * is the only rule that stays correct as types are added: the two drifted
 * once already. `entity.profile.edit` joined the list with MYN-106 AC8's
 * offer review outcomes, which `notification-visibility.ts` scopes to that
 * key - the merged Edit Profile / Perk grant. Without it the partner member
 * the notification is FOR had no bell to receive it in. A holder of that
 * grant on an entity type with no offers simply sees an empty panel, exactly
 * as a `team.invite` holder with no pending joins already does.
 */
export function NotificationBell() {
  const entity = useEntity();
  const canManageShares = usePermission(PERMISSIONS.SHARES_MANAGE);
  // Call every hook unconditionally (rules-of-hooks) rather than short-circuiting
  // with `||` between the usePermission() calls.
  const hasTeamManage = usePermission(PERMISSIONS.TEAM_MANAGE);
  const hasTeamInvite = usePermission(PERMISSIONS.TEAM_INVITE);
  const canEditProfile = usePermission(PERMISSIONS.ENTITY_PROFILE_EDIT);
  // v1.2 (FR-145): the Premium switched-on notice is scoped to billing.manage,
  // and the API's gate admits it for the same reason.
  const canManageBilling = usePermission(PERMISSIONS.BILLING_MANAGE);
  const canManageTeam = hasTeamManage || hasTeamInvite;
  // `can()` short-circuits every permission to true for a Super Admin (AUTHZ-1),
  // which is correct for gating admin-only UI but wrong here: this bell polls
  // the ENTITY-scoped `/notifications` endpoint, which 403s for a Super Admin
  // with no active entity at all. Requiring one (via `useEntity`) alongside the
  // permission checks is what actually reflects whether there is an entity feed
  // to poll - a Super Admin's own admin-audience feed is the separate
  // `AdminNotificationBell`, mounted only inside the admin shell.
  const enabled =
    entity !== null && (canManageShares || canManageTeam || canEditProfile || canManageBilling);

  const notifications = useNotificationsPoll(enabled);
  const { markRead, markAllRead } = useNotificationActions();

  if (!enabled) return null;

  const items = notifications.data ?? [];
  const unreadCount = items.filter((n) => !n.readAt).length;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        >
          <Bell size={20} aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-pink px-1 font-sans text-[10px] font-semibold text-white"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 w-96 rounded-card bg-white py-2 shadow-lg"
        >
          <div className="flex items-center justify-between gap-4 px-4 py-2">
            <span className="font-sans text-sm font-semibold text-navy">Notifications</span>
            {unreadCount > 0 && (
              <DropdownMenu.Item
                onSelect={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="cursor-pointer text-xs font-medium text-dkgreen outline-none hover:text-green data-disabled:cursor-not-allowed data-disabled:opacity-60"
              >
                Mark all as read
              </DropdownMenu.Item>
            )}
          </div>

          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate">No recent activity.</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {items.map((notification) => {
                // Looked up, not called blind. The Record is exhaustive at
                // compile time, but the Backend ships its own enum: three
                // offer types went live against a build that had no copy for
                // them, and `COPY[type](n)` white-screened the whole app for
                // any partner admin holding one. A type this build does not
                // know is now a generic row, not a crash.
                const copy = NOTIFICATION_COPY[notification.type];
                const label = copy ? copy(notification) : UNKNOWN_NOTIFICATION_COPY;
                const isUnread = !notification.readAt;
                const markThisRead = () => {
                  if (isUnread) markRead.mutate(notification.id);
                };

                const linked = isLinkedNotification(notification);

                return (
                  <li
                    key={notification.id}
                    className={`border-t border-divider px-4 py-3 text-sm transition-colors duration-150 ${
                      isUnread ? 'bg-green/5' : ''
                    } ${linked ? 'hover:bg-green/8 has-focus-visible:bg-green/8' : ''}`}
                  >
                    <div className="min-w-0">
                      {linked ? (
                        // DropdownMenu.Item asChild (same pattern as
                        // NavDropdown's nav links) so Radix actually closes
                        // the panel on click - a bare Link inside
                        // DropdownMenu.Content doesn't trigger Radix's own
                        // dismiss behavior, leaving the panel floating over
                        // the page navigated to. Also marks read on click -
                        // navigating away used to leave the alert unread
                        // forever, since only the separate "Mark as read"
                        // action did that. Hover/focus background lives on
                        // the whole row (the <li> above) instead of a tight
                        // box around just this text - a rounded highlight
                        // hugging two wrapped lines looked like a rendering
                        // glitch, not an intentional hover state.
                        <DropdownMenu.Item asChild>
                          <Link to={notification.linkPath} className="block text-navy outline-none" onClick={markThisRead}>
                            {label}
                          </Link>
                        </DropdownMenu.Item>
                      ) : (
                        <p className="text-navy">{label}</p>
                      )}
                      {/* join_requested needs the requester's raw email
                          somewhere - the admin has to paste it into the
                          "Invite a Team Member" dialog to actually act on
                          this. A copy button saves a manual select-all. Not
                          a DropdownMenu.Item (clicking copy shouldn't
                          navigate or close the panel like the label does). */}
                      {isJoinRequestedNotification(notification) && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <span className="truncate text-xs text-slate">
                            {notification.joinRequesterEmail}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyEmail(notification.joinRequesterEmail)}
                            className="shrink-0 cursor-pointer text-slate outline-none hover:text-dkgreen focus-visible:ring-2 focus-visible:ring-dkgreen"
                            aria-label={`Copy ${notification.joinRequesterEmail}`}
                          >
                            <Copy size={12} aria-hidden="true" />
                          </button>
                        </div>
                      )}
                      {/* Timestamp + "Mark as read" share a row (not stacked
                          above/beside the label) to save width - the Link
                          above wraps only the label, so this action is a
                          sibling, never nested inside it. */}
                      <div className="mt-1 flex items-center justify-between gap-3">
                        <span className="text-xs text-slate">
                          {formatDateTime(notification.createdAt)}
                        </span>
                        {isUnread && (
                          <DropdownMenu.Item
                            // Keep the panel open - marking one notification read
                            // shouldn't dismiss the others the user may still want to read.
                            onSelect={(event) => {
                              event.preventDefault();
                              markRead.mutate(notification.id);
                            }}
                            disabled={markRead.isPending && markRead.variables === notification.id}
                            className="shrink-0 cursor-pointer text-xs font-medium text-dkgreen outline-none hover:text-green data-disabled:cursor-not-allowed data-disabled:opacity-60"
                          >
                            Mark as read
                          </DropdownMenu.Item>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
